
import { useState } from 'react';

export default function RegisteredAddress() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);

  const addressTypes = [
    {
      id: 'business-secretary',
      title: '商务秘书地址',
      description: '经政府备案的合法地址，提供专业托管服务',
      icon: 'ri-building-3-line',
      price: '3000-10000元/年',
      features: ['政府备案', '信函代收', '配合核查', '稳定可靠'],
      suitableFor: ['初创企业', '轻资产企业', '注重合规性的企业'],
      advantages: ['合法性强', '服务专业', '稳定性好']
    },
    {
      id: 'incubator-address',
      title: '创业孵化器地址',
      description: '享受创业扶持政策，获得额外资源支持',
      icon: 'ri-rocket-line',
      price: '免费-5000元/年',
      features: ['政策扶持', '创业资源', '专业指导', '网络支持'],
      suitableFor: ['科技型初创企业', '希望获取创业资源的团队'],
      advantages: ['政策优惠', '资源丰富', '成本较低']
    },
    {
      id: 'virtual-address',
      title: '虚拟办公地址',
      description: '成本最低，满足基本注册需求',
      icon: 'ri-map-pin-line',
      price: '1000-3000元/年',
      features: ['成本低廉', '快速办理', '基础服务', '灵活便捷'],
      suitableFor: ['对地址功能要求简单的企业', '预算有限的创业者'],
      advantages: ['价格实惠', '办理快速', '门槛较低']
    },
    {
      id: 'industrial-park',
      title: '产业园区地址',
      description: '享受园区优惠政策和配套服务',
      icon: 'ri-community-line',
      price: '2000-8000元/年',
      features: ['园区政策', '税收优惠', '配套服务', '产业集聚'],
      suitableFor: ['制造业企业', '高新技术企业', '希望享受园区政策的企业'],
      advantages: ['政策优惠', '配套完善', '产业集聚效应']
    }
  ];

  const applicationProcess = [
    {
      step: '1',
      title: '选择服务商',
      description: '核实资质，选择正规备案服务商',
      icon: 'ri-search-line',
      details: ['查验营业执照', '确认备案资质', '了解服务内容', '比较价格方案']
    },
    {
      step: '2',
      title: '签订协议',
      description: '签订正式的地址托管使用协议',
      icon: 'ri-file-text-line',
      details: ['明确使用期限', '确定服务费用', '约定服务内容', '规定双方责任']
    },
    {
      step: '3',
      title: '准备材料',
      description: '准备企业注册所需的相关材料',
      icon: 'ri-folder-line',
      details: ['身份证明文件', '公司名称预核准', '托管协议书', '其他必要材料']
    },
    {
      step: '4',
      title: '工商登记',
      description: '向市场监管局提交注册申请',
      icon: 'ri-government-line',
      details: ['提交申请材料', '缴纳注册费用', '等待审核结果', '配合现场核查']
    },
    {
      step: '5',
      title: '后续维护',
      description: '完成注册后的日常维护工作',
      icon: 'ri-settings-line',
      details: ['处理信函通知', '按时缴纳费用', '及时变更登记', '保持联系畅通']
    }
  ];

  const suitableBusinesses = [
    {
      category: '互联网科技',
      icon: 'ri-computer-line',
      color: 'bg-blue-500',
      businesses: ['电商平台', '软件开发', 'APP开发', '网站建设', '数据服务', '云计算服务'],
      reason: '主要通过线上开展业务，对实体办公场所需求较低'
    },
    {
      category: '咨询服务',
      icon: 'ri-service-line',
      color: 'bg-green-500',
      businesses: ['管理咨询', '财务咨询', '法律咨询', '投资咨询', '技术咨询', '市场调研'],
      reason: '以智力服务为主，可通过远程方式提供专业服务'
    },
    {
      category: '文化创意',
      icon: 'ri-palette-line',
      color: 'bg-purple-500',
      businesses: ['广告设计', '影视制作', '文案策划', '品牌设计', '摄影服务', '艺术创作'],
      reason: '创意型业务，工作地点灵活，适合使用挂靠地址'
    },
    {
      category: '贸易进出口',
      icon: 'ri-ship-line',
      color: 'bg-orange-500',
      businesses: ['国际贸易', '进出口代理', '跨境电商', '供应链管理', '货物代理', '报关服务'],
      reason: '业务多为线上操作和异地执行，无需固定办公场所'
    }
  ];

  const restrictedIndustries = [
    {
      industry: '餐饮服务',
      icon: 'ri-restaurant-line',
      reason: '需要实际经营场所进行食品制作和服务',
      requirements: ['食品经营许可证', '现场核查', '卫生条件审核']
    },
    {
      industry: '物流运输',
      icon: 'ri-truck-line',
      reason: '需要车辆停放场地和货物仓储空间',
      requirements: ['运输许可证', '场地核查', '车辆管理要求']
    },
    {
      industry: '金融服务',
      icon: 'ri-bank-line',
      reason: '监管严格，必须有实际营业场所',
      requirements: ['金融许可证', '营业场所审核', '安全设施要求']
    },
    {
      industry: '医疗器械',
      icon: 'ri-health-book-line',
      reason: '需要专业存储环境和展示场所',
      requirements: ['医疗器械许可证', '存储条件审核', '温湿度控制']
    },
    {
      industry: '教育培训',
      icon: 'ri-school-line',
      reason: '需要教学场所和安全设施',
      requirements: ['办学许可证', '消防安全审核', '教学环境检查']
    },
    {
      industry: '危化品贸易',
      icon: 'ri-flask-line',
      reason: '涉及安全生产，必须有合规存储场所',
      requirements: ['危化品许可证', '安全生产审核', '专业存储设施']
    }
  ];

  const riskPreventions = [
    {
      risk: '地址异常风险',
      icon: 'ri-map-pin-line',
      color: 'text-red-600',
      description: '工商税务部门无法联系企业，可能被列入异常名录',
      preventions: [
        '确保服务商能及时处理信函',
        '保持联系方式畅通',
        '定期查询企业状态',
        '及时处理政府通知'
      ]
    },
    {
      risk: '服务商风险',
      icon: 'ri-user-line',
      color: 'text-orange-600',
      description: '服务商失联或停止服务，影响企业正常运营',
      preventions: [
        '选择有资质的正规服务商',
        '签订详细的服务协议',
        '定期核实服务商状态',
        '准备备用联系方式'
      ]
    },
    {
      risk: '银行开户风险',
      icon: 'ri-bank-card-line',
      color: 'text-blue-600',
      description: '银行对挂靠地址企业审核更严，可能影响开户',
      preventions: [
        '准备充分的证明材料',
        '选择对挂靠地址友好的银行',
        '提前沟通开户要求',
        '考虑实际经营地证明'
      ]
    },
    {
      risk: '行业限制风险',
      icon: 'ri-shield-line',
      color: 'text-purple-600',
      description: '特殊行业不适用挂靠地址，强行使用面临处罚',
      preventions: [
        '详细了解行业监管要求',
        '咨询相关部门政策',
        '评估业务实际需求',
        '必要时选择实际地址'
      ]
    }
  ];

  const detailedGuides = {
    'business-secretary': {
      title: '商务秘书地址详细指南',
      sections: [
        {
          title: '服务内容详解',
          icon: 'ri-service-line',
          content: [
            '【地址托管服务】提供合法的企业注册地址，满足工商登记要求',
            '【信函代收服务】代收工商、税务、银行等部门的各类信函通知',
            '【电话转接服务】提供固定电话号码，并可进行来电转接',
            '【配合核查服务】配合工商、税务等部门的实地核查工作',
            '【年检协助服务】协助企业完成年度报告和各类检查',
            '【变更代办服务】协助办理企业信息变更等工商手续'
          ]
        },
        {
          title: '适用企业类型',
          icon: 'ri-building-line',
          content: [
            '【初创企业】刚成立的公司，希望降低初期运营成本',
            '【轻资产企业】主要从事咨询、设计、软件开发等服务业',
            '【电商企业】主要通过网络平台开展业务的企业',
            '【异地经营企业】总部在外地，需要在广州设立分支机构',
            '【项目型企业】为特定项目设立的临时性公司',
            '【个人工作室】个人创业者或小型团队'
          ]
        },
        {
          title: '费用构成说明',
          icon: 'ri-money-dollar-circle-line',
          content: [
            '【基础托管费】年费3000-8000元，根据地段和服务内容确定',
            '【增值服务费】电话转接、快递代收等服务可能收取额外费用',
            '【押金费用】部分服务商要求缴纳1000-3000元押金',
            '【工本费用】协议签署、证明文件等可能产生少量工本费',
            '【续约费用】续约时可能享受优惠价格',
            '【违约费用】提前解约或违反协议条款可能产生违约金'
          ]
        },
        {
          title: '选择标准建议',
          icon: 'ri-check-double-line',
          content: [
            '【资质审核】优先选择有商务秘书企业备案的正规服务商',
            '【地址位置】选择交通便利、商务氛围浓厚的地段',
            '【服务内容】根据企业需求选择合适的服务套餐',
            '【价格对比】综合比较不同服务商的价格和服务质量',
            '【口碑评价】了解其他客户的使用体验和评价',
            '【合同条款】仔细审查合同条款，确保权利义务明确'
          ]
        }
      ]
    },
    'incubator-address': {
      title: '创业孵化器地址详细指南',
      sections: [
        {
          title: '孵化器类型分类',
          icon: 'ri-building-4-line',
          content: [
            '【综合型孵化器】面向各行业创业企业，提供通用创业服务',
            '【专业型孵化器】专注特定行业，如科技、文创、电商等',
            '【大学科技园】依托高校资源，重点扶持科技成果转化',
            '【企业孵化器】大型企业设立，重点培育产业链相关项目',
            '【政府孵化器】政府主导设立，享受更多政策扶持',
            '【民营孵化器】民间资本运营，服务更加灵活多样'
          ]
        },
        {
          title: '入驻条件要求',
          icon: 'ri-checkbox-circle-line',
          content: [
            '【企业阶段要求】通常要求为初创期企业，成立时间不超过3年',
            '【行业领域要求】符合孵化器的专业定位和发展方向',
            '【团队要求】有完整的创业团队和明确的商业模式',
            '【项目要求】具有创新性和市场前景的创业项目',
            '【资金要求】有一定的启动资金和融资计划',
            '【承诺要求】承诺在孵化器内办公一定期限'
          ]
        },
        {
          title: '享受政策支持',
          icon: 'ri-gift-line',
          content: [
            '【租金优惠】享受场地租金减免或优惠政策',
            '【资金扶持】可申请创业资助、无息贷款等资金支持',
            '【税收优惠】享受小微企业税收减免政策',
            '【培训服务】免费参加创业培训、管理培训等',
            '【导师指导】享受创业导师一对一指导服务',
            '【资源对接】优先获得投资机构、合作伙伴推荐'
          ]
        },
        {
          title: '申请流程详解',
          icon: 'ri-route-line',
          content: [
            '第1步：项目评估 - 准备商业计划书，参加孵化器的项目评审',
            '第2步：面试答辩 - 通过初审后参加面试，展示项目优势',
            '第3步：签署协议 - 与孵化器签署入驻协议和相关服务合同',
            '第4步：完成注册 - 使用孵化器地址完成企业工商注册',
            '第5步：入驻办公 - 按协议要求入驻孵化器开展业务',
            '第6步：享受服务 - 参与孵化器提供的各项培训和服务活动'
          ]
        }
      ]
    },
    'virtual-address': {
      title: '虚拟办公地址详细指南',
      sections: [
        {
          title: '服务特点说明',
          icon: 'ri-information-line',
          content: [
            '【成本优势】价格最为经济实惠，适合预算有限的创业者',
            '【快速办理】手续简单，通常1-3天即可完成签约',
            '【基础服务】主要提供注册地址，附加服务相对较少',
            '【灵活性强】使用期限灵活，可根据需要选择时长',
            '【门槛较低】对企业类型和规模限制较少',
            '【适用面广】适合各种轻资产的服务型企业'
          ]
        },
        {
          title: '服务内容范围',
          icon: 'ri-list-check',
          content: [
            '【注册地址提供】提供合法的企业注册地址',
            '【基础信函代收】代收重要的政府部门信函',
            '【电话留言服务】提供固定电话号码和留言服务',
            '【简单核查配合】配合基本的工商核查工作',
            '【地址证明出具】提供地址使用证明等文件',
            '【续约提醒服务】及时提醒合同到期和续约事宜'
          ]
        },
        {
          title: '注意事项提醒',
          icon: 'ri-alert-line',
          content: [
            '【服务限制】相比商务秘书地址，服务内容相对简单',
            '【稳定性考虑】部分虚拟地址提供商稳定性可能不如大型服务商',
            '【后续服务】如需更多增值服务，可能需要额外付费',
            '【合规性确认】务必确认地址提供商的合法资质',
            '【银行开户】部分银行对虚拟地址的审查可能更严格',
            '【长期规划】如企业快速发展，需考虑后续的地址升级'
          ]
        }
      ]
    }
  };

  // 如果选择了详细指南，显示指南内容
  if (selectedGuide && detailedGuides[selectedGuide as keyof typeof detailedGuides]) {
    const guide = detailedGuides[selectedGuide as keyof typeof detailedGuides];
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSelectedGuide(null)}
                  className="text-orange-600 hover:text-orange-800 cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{guide.title}</h1>
                  <p className="text-lg text-gray-600 mt-2">专业挂靠地址服务详解</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap">
                  收藏指南
                </button>
                <button className="border border-orange-600 text-orange-600 px-6 py-2 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap">
                  打印指南
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Guide Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">指南目录</h3>
                <nav className="space-y-2">
                  {guide.sections.map((section, index) => (
                    <a
                      key={index}
                      href={`#section-${index}`}
                      className="flex items-center p-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className={`${section.icon} mr-3 text-orange-600`}></i>
                      <span className="text-sm font-medium">{section.title}</span>
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="space-y-8">
                {guide.sections.map((section, index) => (
                  <div
                    key={index}
                    id={`section-${index}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-orange-500 to-red-600 px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                          <i className={`${section.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-white">{section.title}</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {section.content.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-gray-700 leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="mt-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl p-6 text-white">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">需要挂靠地址服务？</h3>
                  <p className="text-orange-100 mb-6">我们为您提供多种类型的挂靠地址解决方案</p>
                  <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <button className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                      立即咨询服务
                    </button>
                    <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                      获取报价方案
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="text-orange-600 hover:text-orange-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">挂靠地址培训</h1>
                <p className="text-lg text-gray-600 mt-2">企业注册地址托管服务专业指导</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-orange-100 px-4 py-2 rounded-full">
                <span className="text-orange-800 font-medium">学习进度: 0%</span>
              </div>
              <button className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap">
                开始学习
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/8f53775b28425b03435dedb051486f40.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-orange-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            专业挂靠地址服务
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
            合法合规的企业注册地址解决方案，降低创业成本，助力企业快速起步
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">合法合规</div>
              <div className="text-orange-100">政府备案认可</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">成本节省</div>
              <div className="text-orange-100">降低运营成本</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">服务专业</div>
              <div className="text-orange-100">全程托管服务</div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Definition */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">什么是挂靠地址</h3>
            <p className="text-lg text-gray-600">了解挂靠地址的定义、合法性和适用情况</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="bg-orange-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-information-line text-white text-xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">业务定义</h4>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  挂靠地址是指企业将注册地址登记在合法的商务秘书公司、创业孵化器或产业园区提供的地址上，
                  而无需租赁实际办公场地的一种注册方式。这种方式也被称为"地址托管"或"集群注册"。
                </p>
              </div>
              
              <div className="bg-green-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-white text-xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">合法性确认</h4>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  在合法合规的前提下，挂靠地址是被允许的。广州市自2018年起推行的商事登记制度改革，
                  明确允许"一址多照"和集群登记模式，使得挂靠地址成为合法的注册选择。
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-building-line text-white text-xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">政策依据</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    <span className="text-gray-700">商事登记制度改革政策支持</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    <span className="text-gray-700">"一址多照"集群登记模式</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    <span className="text-gray-700">商务秘书企业备案制度</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    <span className="text-gray-700">创业孵化器政策扶持</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-lightbulb-line text-white text-xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">核心价值</h4>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li>• 降低企业初期运营成本</li>
                  <li>• 简化企业注册流程</li>
                  <li>• 提供专业托管服务</li>
                  <li>• 享受政策扶持优惠</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Address Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">挂靠地址类型</h3>
            <p className="text-lg text-gray-600">多种地址类型，满足不同企业需求</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {addressTypes.map((address, index) => (
              <div key={address.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${address.icon} text-orange-600 text-2xl`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{address.title}</h4>
                      <p className="text-gray-600 text-sm">{address.description}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">价格范围</span>
                        <span className="text-orange-600 font-bold">{address.price}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">服务特色</h5>
                    <div className="flex flex-wrap gap-2">
                      {address.features.map((feature, featureIndex) => (
                        <span 
                          key={featureIndex}
                          className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-sm"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">适用企业</h5>
                    <div className="space-y-1">
                      {address.suitableFor.map((suitable, suitableIndex) => (
                        <div key={suitableIndex} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-2"></div>
                          {suitable}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="font-semibold text-gray-900 mb-2">主要优势</h5>
                    <div className="grid grid-cols-3 gap-2">
                      {address.advantages.map((advantage, advantageIndex) => (
                        <div key={advantageIndex} className="text-center">
                          <div className="bg-orange-100 p-2 rounded-lg">
                            <span className="text-xs text-orange-700 font-medium">{advantage}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setSelectedGuide(address.id)}
                    className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看详细指南
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">办理流程</h3>
            <p className="text-lg text-gray-600">标准化的申请流程，确保顺利办理</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {applicationProcess.map((process, index) => (
              <div key={process.step} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${process.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{process.step}</span>
                  </div>
                  {index < applicationProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-orange-200 -ml-10"></div>
                  )}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h4>
                <p className="text-gray-600 mb-4">{process.description}</p>
                <div className="space-y-1">
                  {process.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="text-sm text-gray-500">
                      • {detail}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suitable vs Restricted Industries */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">适用行业分析</h3>
            <p className="text-lg text-gray-600">了解哪些行业适合使用挂靠地址，哪些行业不适用</p>
          </div>
          
          {/* Suitable Industries */}
          <div className="mb-16">
            <h4 className="text-2xl font-bold text-gray-900 mb-8 text-center">✅ 适用行业</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {suitableBusinesses.map((category, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className={`${category.color} p-4`}>
                    <div className="flex items-center justify-center text-white">
                      <i className={`${category.icon} text-2xl mr-3`}></i>
                      <h5 className="text-lg font-bold">{category.category}</h5>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3 mb-4">
                      {category.businesses.map((business, businessIndex) => (
                        <div key={businessIndex} className="flex items-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                          <span className="text-gray-700 text-sm">{business}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-xs text-green-800">{category.reason}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Restricted Industries */}
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-8 text-center">❌ 限制行业</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {restrictedIndustries.map((industry, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${industry.icon} text-red-600 text-xl`}></i>
                    </div>
                    <h5 className="text-lg font-bold text-gray-900">{industry.industry}</h5>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{industry.reason}</p>
                  <div className="space-y-2">
                    {industry.requirements.map((requirement, reqIndex) => (
                      <div key={reqIndex} className="flex items-center">
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                        <span className="text-gray-700 text-sm">{requirement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Risk Prevention */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">风险防范措施</h3>
            <p className="text-lg text-gray-600">了解潜在风险，采取有效防范措施</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {riskPreventions.map((risk, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg border-l-4 border-orange-500 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${risk.icon} ${risk.color} text-xl`}></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">{risk.risk}</h4>
                  </div>
                  <p className="text-gray-600 mb-4">{risk.description}</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-semibold text-gray-900 mb-3">防范措施：</h5>
                    <div className="space-y-2">
                      {risk.preventions.map((prevention, preventionIndex) => (
                        <div key={preventionIndex} className="flex items-start">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-gray-700 text-sm">{prevention}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
            <div className="flex items-center mb-2">
              <i className="ri-lightbulb-line text-amber-600 text-xl mr-2"></i>
              <h4 className="text-lg font-semibold text-amber-800">总结建议</h4>
            </div>
            <p className="text-amber-700">
              挂靠地址对于符合条件的初创企业和轻资产公司来说，是一项合法且能有效降低创业初期成本的便利措施。
              选择此项服务时，务必谨慎选择服务商，详细了解协议内容，并做好后续的维护工作，以确保公司的合规经营。
            </p>
          </div>
        </div>
      </section>

      {/* Service Advantages */}
      <section className="py-16 bg-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">服务优势</h3>
            <p className="text-xl text-orange-100">选择专业挂靠地址服务的理由</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">合法合规</h4>
              <p className="text-orange-100 text-sm">政府备案认可的合法地址</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">成本节约</h4>
              <p className="text-orange-100 text-sm">大幅降低企业运营成本</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">专业服务</h4>
              <p className="text-orange-100 text-sm">提供全方位托管服务</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">快速办理</h4>
              <p className="text-orange-100 text-sm">简化流程，快速完成注册</p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">重要注意事项</h3>
            <p className="text-lg text-gray-600">使用挂靠地址服务的关键要点</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900">合规性要求</h4>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 必须选择经政府备案的正规服务商</li>
                <li>• 确保服务协议条款清晰明确</li>
                <li>• 按时处理政府部门的各类通知</li>
                <li>• 保持企业信息的真实有效</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-time-line text-orange-600 text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900">时效性管理</h4>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 注意地址托管协议的有效期</li>
                <li>• 及时办理协议续签手续</li>
                <li>• 定期检查企业经营状态</li>
                <li>• 适时考虑地址变更需求</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-shield-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900">风险防控</h4>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 建立良好的沟通机制</li>
                <li>• 准备应急联系方式</li>
                <li>• 定期评估服务质量</li>
                <li>• 关注政策变化动态</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-white mb-4">
            专业挂靠地址服务，助力企业快速起步
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            选择合适的挂靠地址，让创业之路更加顺畅
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button className="bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap">
              立即咨询服务
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              获取报价方案
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">联系我们</h4>
              <div className="space-y-2 text-gray-300">
                <p>电话: 020-1234-5678</p>
                <p>邮箱: address@example.com</p>
                <p>地址: 广州市天河区xxx路123号</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">快速链接</h4>
              <div className="space-y-2 text-gray-300">
                <p className="cursor-pointer hover:text-white" onClick={() => window.REACT_APP_NAVIGATE('/')}>返回首页</p>
                <p>服务对比</p>
                <p>价格咨询</p>
                <p>常见问题</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">地址类型</h4>
              <div className="space-y-2 text-gray-300">
                <p>商务秘书地址</p>
                <p>创业孵化器地址</p>
                <p>虚拟办公地址</p>
                <p>产业园区地址</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">增值服务</h4>
              <div className="space-y-2 text-gray-300">
                <p>信函代收</p>
                <p>电话转接</p>
                <p>工商代办</p>
                <p><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Made with Readdy</a></p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 企服培训平台. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
